import type { TaxResult } from "../types/tax.types";
import BracketChart from "./BracketChart";
import { cn } from "@/lib/utils";

function fmt(n: number) {
  return n.toLocaleString("th-TH");
}

function MetricRow({
  label,
  value,
  sub,
  highlight,
  danger,
  success,
}: {
  label: string;
  value: string;
  sub?: string;
  highlight?: boolean;
  danger?: boolean;
  success?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-baseline justify-between gap-2 py-2.5 border-b border-border last:border-0",
        highlight && "py-3",
      )}
    >
      <span className="text-[13px] text-muted-foreground leading-snug">
        {label}
      </span>
      <div className="text-right">
        <div
          className={cn(
            "font-sans [font-variant-numeric:tabular-nums] leading-snug",
            highlight
              ? "text-[18px] font-bold text-foreground"
              : "text-[14px] font-semibold text-foreground",
            danger && "text-[var(--color-expense)]",
            success && "text-[var(--color-income)]",
          )}
        >
          {value}
        </div>
        {sub && (
          <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>
        )}
      </div>
    </div>
  );
}

export default function TaxSummarySidebar({
  result,
  ceYear,
}: {
  result: TaxResult;
  ceYear: number;
}) {
  const hasRefund = result.refund > 0;

  return (
    <div className="space-y-4">
      {/* Summary card */}
      <div className="rounded-[14px] border border-border bg-card shadow-sm p-4 sm:p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[15px] font-semibold text-foreground">
            Tax Summary {ceYear}
          </h2>
          <span className="text-[12px] text-muted-foreground px-2 py-0.5 rounded-full bg-muted">
            Tax Year {ceYear}
          </span>
        </div>

        <div className="divide-y divide-border">
          <MetricRow
            label="Total Income"
            value={`฿${fmt(result.totalGrossIncome)}`}
          />
          <MetricRow
            label="Total Deductions"
            value={`฿${fmt(result.totalDeductions)}`}
          />
          <MetricRow
            label="Net Taxable Income"
            value={`฿${fmt(result.netTaxableIncome)}`}
            highlight
          />
          <MetricRow
            label="Estimated Tax"
            value={`฿${fmt(result.grossTax)}`}
            danger={result.grossTax > 0}
          />
          <MetricRow
            label="Withholding Tax Credit"
            value={`−฿${fmt(result.withholdingCredit)}`}
          />
        </div>

        {/* Net tax / refund */}
        <div
          className={cn(
            "mt-4 rounded-[10px] p-4 text-center",
            hasRefund
              ? "bg-[var(--color-success-bg)]"
              : result.netTax > 0
                ? "bg-[var(--color-danger-bg)]"
                : "bg-muted",
          )}
        >
          <div className="text-[12px] font-medium mb-1">
            {hasRefund
              ? "Tax Refund"
              : result.netTax > 0
                ? "Tax Due"
                : "Balanced"}
          </div>
          <div
            className={cn(
              "text-[28px] font-bold font-sans [font-variant-numeric:tabular-nums]",
              hasRefund
                ? "text-[var(--color-success-fg)]"
                : result.netTax > 0
                  ? "text-[var(--color-expense)]"
                  : "text-muted-foreground",
            )}
          >
            {hasRefund ? "+" : ""}฿
            {fmt(hasRefund ? result.refund : result.netTax)}
          </div>
          {result.totalGrossIncome > 0 && (
            <div className="text-[12px] text-muted-foreground mt-1">
              Effective rate {(result.effectiveRate * 100).toFixed(2)}%
            </div>
          )}
        </div>
      </div>

      {/* Bracket chart card */}
      <div className="rounded-[14px] border border-border bg-card shadow-sm p-4 sm:p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[15px] font-semibold text-foreground">
            Tax Brackets
          </h2>
          <span className="text-[12px] text-muted-foreground">
            Total ฿{fmt(result.grossTax)}
          </span>
        </div>
        <div className="text-[10px] text-muted-foreground flex gap-2 mb-3">
          <span>Rate</span>
          <span className="flex-1" />
          <span>Tax</span>
        </div>
        <BracketChart brackets={result.brackets} />
      </div>

      {/* Deduction breakdown */}
      <div className="rounded-[14px] border border-border bg-card shadow-sm p-4 sm:p-5">
        <h2 className="text-[15px] font-semibold text-foreground mb-3">
          Deduction Breakdown
        </h2>
        <div className="space-y-1.5 text-[13px]">
          {result.totalExpenseDeduction > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                Expense deduction (40(1)/(8))
              </span>
              <span className="font-sans [font-variant-numeric:tabular-nums] text-foreground">
                ฿{fmt(result.totalExpenseDeduction)}
              </span>
            </div>
          )}
          {result.personalAllowance > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Personal allowance</span>
              <span className="font-sans [font-variant-numeric:tabular-nums] text-foreground">
                ฿{fmt(result.personalAllowance)}
              </span>
            </div>
          )}
          {result.spouseAllowance > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Spouse</span>
              <span className="font-sans [font-variant-numeric:tabular-nums] text-foreground">
                ฿{fmt(result.spouseAllowance)}
              </span>
            </div>
          )}
          {result.childrenAllowance > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Children</span>
              <span className="font-sans [font-variant-numeric:tabular-nums] text-foreground">
                ฿{fmt(result.childrenAllowance)}
              </span>
            </div>
          )}
          {result.parentSupportAllowance > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Parent support</span>
              <span className="font-sans [font-variant-numeric:tabular-nums] text-foreground">
                ฿{fmt(result.parentSupportAllowance)}
              </span>
            </div>
          )}
          {result.disabledSupportAllowance > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Disabled support</span>
              <span className="font-sans [font-variant-numeric:tabular-nums] text-foreground">
                ฿{fmt(result.disabledSupportAllowance)}
              </span>
            </div>
          )}
          {result.lifeInsuranceDeduction + result.healthInsuranceDeduction >
            0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                Life / health insurance
              </span>
              <span className="font-sans [font-variant-numeric:tabular-nums] text-foreground">
                ฿
                {fmt(
                  result.lifeInsuranceDeduction +
                    result.healthInsuranceDeduction,
                )}
              </span>
            </div>
          )}
          {result.ssoDeduction > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">SSO</span>
              <span className="font-sans [font-variant-numeric:tabular-nums] text-foreground">
                ฿{fmt(result.ssoDeduction)}
              </span>
            </div>
          )}
          {result.retirementCombined > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">PVD + RMF + SSF</span>
              <span className="font-sans [font-variant-numeric:tabular-nums] text-foreground">
                ฿{fmt(result.retirementCombined)}
              </span>
            </div>
          )}
          {result.tesgDeduction > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">TESG</span>
              <span className="font-sans [font-variant-numeric:tabular-nums] text-foreground">
                ฿{fmt(result.tesgDeduction)}
              </span>
            </div>
          )}
          {result.homeLoanInterestDeduction > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Home loan interest</span>
              <span className="font-sans [font-variant-numeric:tabular-nums] text-foreground">
                ฿{fmt(result.homeLoanInterestDeduction)}
              </span>
            </div>
          )}
          {result.easyEReceiptDeduction > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Easy e-Receipt</span>
              <span className="font-sans [font-variant-numeric:tabular-nums] text-foreground">
                ฿{fmt(result.easyEReceiptDeduction)}
              </span>
            </div>
          )}
          {result.educationDonationDeduction > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                Education / sport donation
              </span>
              <span className="font-sans [font-variant-numeric:tabular-nums] text-foreground">
                ฿{fmt(result.educationDonationDeduction)}
              </span>
            </div>
          )}
          {result.politicalDonationDeduction > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Political donation</span>
              <span className="font-sans [font-variant-numeric:tabular-nums] text-foreground">
                ฿{fmt(result.politicalDonationDeduction)}
              </span>
            </div>
          )}
          <div className="flex justify-between pt-2 border-t border-border font-semibold">
            <span className="text-foreground">Total deductions</span>
            <span className="font-sans [font-variant-numeric:tabular-nums] text-foreground">
              ฿{fmt(result.totalDeductions)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
